/**
 * Find the item in a list whose `key` property equals `value`.
 *
 * Used in the map-selection flow to resolve the currently selected project,
 * company and office from their dropdown ids. The selection MUST be driven by
 * the current dropdown ids, not by previously cached officeStore values —
 * otherwise re-selecting leaves the officeStore on a stale (and, across a
 * project switch, inconsistent) project/company/office triple, which produces
 * "No paths found" / "No metric data" errors
 * (see bugfix/office-marker-not-updating-on-reselect).
 *
 * @param list list of items (e.g. projects, companies, offices)
 * @param key property name that holds the id (e.g. "id" or "office_id")
 * @param value the currently selected id
 * @returns the matching item, or `null` if none matches
 */
export function findByKey(
  list: unknown,
  key: string,
  value: number | string | null | undefined,
): Record<string, any> | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!Array.isArray(list)) {
    return null;
  }
  const match = list.find(
    (item) => item && (item as Record<string, any>)[key] === value,
  );
  return (match as Record<string, any>) ?? null;
}

/**
 * Resolve the office matching a given office id from a list of offices.
 * Thin wrapper around {@link findByKey} for the `office_id` key.
 */
export function resolveOfficeById(
  offices: unknown,
  officeId: number | null | undefined,
): Record<string, any> | null {
  return findByKey(offices, "office_id", officeId);
}
